import { defineStore, storeToRefs } from "pinia";
import { type Ref, computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { useApplicationManager } from "@/composables/application-manager";
import { useDomainManager } from "@/composables/domain-manager";
import { useDomainTermManager } from "@/composables/domain-term-manager";
import { useEditManager } from "@/composables/edit-manager";
import {
    useProcessManager,
    useProcessStore,
} from "@/composables/process-manager";
import { useProductManager } from "@/composables/product-manager";
import { useRoleManager } from "@/composables/role-manager";
import { Artefact, ArtefactMeta } from "@/lib/artefacts";
import {
    HistoryEntryWithDate,
    useArtefactManager,
    useArtefactsManager,
    useArtefactsStore,
    withContent,
} from "@/lib/artefacts/user";
import { ContentFlavor, getMimetypeFlavor } from "@/lib/contentFlavor";
import { useClient } from "@/lib/openapi";
import { useBusinessEntityManager } from "@/stores/business-entity-store";
import { useEventManager } from "@/stores/event-store";
import { useFlowManager } from "@/stores/flow-store";
import { useGatewayManager } from "@/stores/gateway-store";
import { useTaskManager } from "@/stores/task-store";

import { EntityType, useCurrentEntityManager } from "./entity-store";

interface GroupedHistoryEntries {
    description: string;
    commits: HistoryEntryWithDate[];
}

const second = 1000;
const minute = 60 * second;
const hour = 60 * minute;
const day = 24 * hour;
function getWeek(date: Date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / day;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

function groupDates(historyEntries: HistoryEntryWithDate[]) {
    const today = new Date();
    const thisWeek = getWeek(today);
    const thisMonth = today.getMonth();

    const groups: { [groupName: string]: HistoryEntryWithDate[] } = {};
    historyEntries
        .sort((a, b) => {
            return a.author.date > b.author.date ? -1 : 1;
        })
        .forEach((entry) => {
            const {
                author: { date },
            } = entry;
            const week = getWeek(date);
            const month = date.getMonth();

            // First define the group
            let groupName: string;
            if (week === thisWeek) {
                groupName = "This week";
            } else if (week === thisWeek - 1) {
                groupName = "Last week";
            } else if (month === thisMonth) {
                groupName = "This month";
            } else {
                groupName = date.toLocaleString(undefined, {
                    month: "long",
                });
            }
            // Now add it to the proper group.
            if (groups[groupName]) {
                groups[groupName].push(entry);
            } else {
                groups[groupName] = [entry];
            }
        });

    const orderedGroups: GroupedHistoryEntries[] = [];
    Object.entries(groups).forEach(([groupName, commits]) => {
        orderedGroups.push({ description: groupName, commits });
    });

    return orderedGroups;
}

export const useHistoryStore = defineStore("history", () => {
    const route = useRoute();
    const isFetchingHistory = ref(false);
    const isKnownContentFlavor = ref(true);

    // When changes are shown, the version before the selected version needs to
    // be fetched as well.
    const isShowingChanges = ref(false);
    const artefact = ref<Artefact>();
    const { entity } = useCurrentEntityManager();
    const { artefacts } = useArtefactsManager();

    watch(
        [() => route.name, () => entity.value?.identity, artefacts],
        async () => {
            artefact.value = undefined;
            // Entities with no single editable file (domain/product/role/…) walk
            // their description artefact's git history (decision-annotated by
            // get_history). It loads async, so track it reactively.
            const trackDescription = (
                description: Ref<Artefact<"text/markdown"> | undefined>
            ) => {
                watch(
                    description,
                    (newDescription) => {
                        artefact.value = newDescription;
                    },
                    { immediate: true }
                );
            };
            if (entity.value?.entity_type === EntityType.PROCESS) {
                const { definition } = useProcessManager();
                // Ensure the process is actually loaded
                const { updateProcess } = useProcessStore();
                await updateProcess();
                artefact.value = definition.value;
            } else if (entity.value?.entity_type === EntityType.ARTEFACT) {
                const { artefact: artefactMeta } = useArtefactManager();
                isKnownContentFlavor.value =
                    getMimetypeFlavor(artefactMeta.value?.mimetype) !==
                    ContentFlavor.UNKNOWN;

                const { artefact: artefactWithContent } = withContent(
                    artefactMeta,
                    isKnownContentFlavor,
                    true
                );

                // Copy over artefact value as it's also a ref in itself.
                watch(artefactWithContent, (newArtefact) => {
                    artefact.value = newArtefact;
                });
            } else if (entity.value?.entity_type === EntityType.DOMAIN) {
                trackDescription(useDomainManager().description);
            } else if (entity.value?.entity_type === EntityType.PRODUCT) {
                trackDescription(useProductManager().description);
            } else if (entity.value?.entity_type === EntityType.ROLE) {
                trackDescription(useRoleManager().description);
            } else if (entity.value?.entity_type === EntityType.DOMAIN_TERM) {
                trackDescription(useDomainTermManager().description);
            } else if (entity.value?.entity_type === EntityType.APPLICATION) {
                trackDescription(useApplicationManager().description);
            } else if (
                entity.value?.entity_type === EntityType.BUSINESS_ENTITY
            ) {
                trackDescription(useBusinessEntityManager().description);
            } else if (entity.value?.entity_type === EntityType.TASK) {
                trackDescription(useTaskManager().description);
            } else if (entity.value?.entity_type === EntityType.GATEWAY) {
                trackDescription(useGatewayManager().description);
            } else if (entity.value?.entity_type === EntityType.EVENT) {
                trackDescription(useEventManager().description);
            } else if (entity.value?.entity_type === EntityType.FLOW) {
                trackDescription(useFlowManager().description);
            }
        },
        { immediate: true, deep: true }
    );

    const { isEditing } = useEditManager();
    const artefactsStore = useArtefactsStore();
    const client = useClient({
        requires: { auth: true, organization: true, stek: false },
    });
    const history = ref<HistoryEntryWithDate[]>([]);

    const isViewingHistory = computed(
        () => !!("version" in route.params && route.params.version)
    );

    // Container entities walk their WHOLE-SUBTREE history (the entity + its
    // children: added terms/roles/products/processes), grouped by decision —
    // not just their own description artefact. Single-file entities (process
    // definition, document) keep the per-artefact history + version content.
    const SUBTREE_HISTORY_TYPES = new Set<EntityType>([
        EntityType.DOMAIN,
        EntityType.PRODUCT,
        EntityType.ROLE,
        EntityType.DOMAIN_TERM,
        EntityType.APPLICATION,
        EntityType.BUSINESS_ENTITY,
        EntityType.TASK,
        EntityType.GATEWAY,
        EntityType.EVENT,
        EntityType.FLOW,
    ]);

    const _withDates = (
        entries: HistoryEntryWithDate[]
    ): HistoryEntryWithDate[] =>
        entries.map((entry) => ({
            ...entry,
            author: { ...entry.author, date: new Date(entry.author.date) },
            committer: {
                ...entry.committer,
                date: new Date(entry.committer.date),
            },
        }));

    // Whole-entity subtree history via /biz/entities/{type}/{id}/history
    // (decision-annotated server-side). Low-level client so no OpenAPI regen
    // is needed for the endpoint.
    const fetchEntitySubtreeHistory = async (
        entityType: EntityType,
        entityId: string
    ): Promise<HistoryEntryWithDate[]> => {
        const { data, response } = await client.get({
            url: "/biz/entities/{entity_type}/{entity_id}/history",
            path: { entity_type: entityType, entity_id: entityId },
            query: { limit: 100 },
            throwOnError: false,
        });
        const body = data as { history?: HistoryEntryWithDate[] } | undefined;
        if (!response.ok || !body?.history) {
            return [];
        }
        return _withDates(body.history);
    };

    const fetchHistory = async () => {
        // Avoid retrieving history when the history sidebar isn't opened yet.
        if (!isViewingHistory.value) {
            return;
        }

        const currentEntity = entity.value;
        isFetchingHistory.value = true;
        try {
            if (
                currentEntity &&
                SUBTREE_HISTORY_TYPES.has(currentEntity.entity_type)
            ) {
                history.value = await fetchEntitySubtreeHistory(
                    currentEntity.entity_type,
                    currentEntity.identity
                );
                return;
            }
            if (!artefact.value) {
                return;
            }
            history.value = await artefactsStore.getHistory(artefact.value);
        } catch (e) {
            // Sometimes the history doesn't immediately exist. Instead of
            // throwing an error, silently show an empty history. Something
            // we should fix. mystekz#138
            if (e instanceof Error && e.message.includes("404")) {
                history.value = [];
            } else {
                throw e;
            }
        } finally {
            isFetchingHistory.value = false;
        }
    };

    watch(
        [
            isViewingHistory,
            () => artefact.value?.identity,
            () => entity.value?.identity,
        ],
        () => fetchHistory()
    );

    /**
     * The most recent available in the history.
     */
    const lastCommit = computed(() => {
        if (!history.value) {
            return null;
        }

        return history.value[0];
    });

    /**
     * Get the currently selected commit.
     */
    const selectedCommit = computed(() => {
        if (route.params.version === "current") {
            return history.value[0];
        } else {
            return history.value.find(
                (entry) => entry.id === route.params.version
            );
        }
    });

    /**
     * The commit previous to the selected commit.
     */
    const previousCommit = computed(() => {
        const index = history.value.findIndex(
            (entry) => entry.id === selectedCommit.value?.id
        );

        if (index === -1) {
            return;
        }

        if (index >= history.value.length - 1) {
            return;
        }

        return history.value[index + 1];
    });

    const groupedHistoryEntries = computed<GroupedHistoryEntries[]>(() => {
        if (!history.value) {
            return [];
        }

        return groupDates(history.value);
    });

    const isViewingLatestVersion = computed(() => {
        if (!history.value) {
            return true;
        }

        // While there is either no history or we're still fetching the
        // history, assume we're not viewing an older version.
        if (history.value.length === 0) {
            return true;
        }

        if (!isViewingHistory.value) {
            return true;
        }

        if (route.params.version === "current") {
            return true;
        }

        // When we haven't modified the artefact yet and the param version is
        // the same as the latest commit, we're viewing the latest version.
        if (
            !isEditing.value &&
            selectedCommit.value?.id === lastCommit.value?.id
        ) {
            return true;
        }

        return false;
    });

    const selectedHistoricalArtefactMeta = computed<ArtefactMeta | undefined>(
        () => {
            if (!isViewingHistory.value) {
                return;
            }

            if (!artefact.value) {
                return;
            }

            if (!selectedCommit.value) {
                return;
            }

            const copyOfArtefact = { ...artefact.value } as ArtefactMeta;
            copyOfArtefact.revision = selectedCommit.value.id;

            return copyOfArtefact;
        }
    );

    const {
        artefact: _selectedHistoricalArtefact,
        isFetching: isFetchingHistoricalArtefact,
    } = withContent(selectedHistoricalArtefactMeta, isKnownContentFlavor);

    const selectedArtefact = computed(() => {
        return _selectedHistoricalArtefact.value;
    });

    // One entry before the selected historical artefact.
    const comparisonArtefactMeta = computed<ArtefactMeta | undefined>(() => {
        if (!selectedArtefact.value) {
            return;
        }

        if (!previousCommit.value) {
            return;
        }

        const copyOfArtefact = { ...artefact.value } as ArtefactMeta;
        copyOfArtefact.revision = previousCommit.value.id;

        return copyOfArtefact;
    });

    const {
        artefact: comparisonArtefact,
        isFetching: isFetchingComparisonArtefact,
    } = withContent(comparisonArtefactMeta);

    return {
        selectedCommit,
        previousCommit,
        lastCommit,
        groupedHistoryEntries,
        isViewingLatestVersion,
        isViewingHistory,
        fetchHistory,
        selectedArtefact,
        comparisonArtefact,
        isFetchingHistory,
        isShowingChanges,
        isFetchingHistoricalArtefact: computed(
            () =>
                !!(
                    isFetchingHistoricalArtefact.value ||
                    isFetchingComparisonArtefact.value
                )
        ),
    };
});

export const useHistoryManager = () => storeToRefs(useHistoryStore());
